import { create } from 'zustand';
import MiniSearch from 'minisearch';
import {
  type Channel,
  type Country,
  type Category,
  buildSearchIndex,
  searchChannels,
  applyFeaturedTags,
  extractCountries,
  extractCategories,
  sortChannelsByRelevance,
} from '@/lib/channels';
import { parseM3U } from '@/lib/m3uParser';
import { fetchM3U, M3U_SPORTS_URL, M3U_ALL_URL } from '@/lib/m3uFetcher';

type ChannelsState = {
  sportsChannels: Channel[];
  sportsIndex: MiniSearch<Channel> | null;
  countries: Country[];
  categories: Category[];
  allChannels: Channel[] | null;
  allIndex: MiniSearch<Channel> | null;
  isLoadingInitial: boolean;
  isLoadingAll: boolean;
  isRefreshing: boolean;
  isInitialLoaded: boolean;
  lastUpdated: number | null;
  error: string | null;
  loadInitial: () => Promise<void>;
  loadAll: () => Promise<void>;
  refresh: () => Promise<void>;
  searchSports: (q: string) => Channel[];
  searchAll: (q: string) => Channel[];
};

function parseAndIndex(text: string): Channel[] {
  const raw = parseM3U(text);
  const tagged = applyFeaturedTags(raw);
  return sortChannelsByRelevance(tagged);
}

function buildMetadata(
  channels: Channel[],
): { index: MiniSearch<Channel>; countries: Country[]; categories: Category[] } {
  return {
    index: buildSearchIndex(channels),
    countries: extractCountries(channels),
    categories: extractCategories(channels),
  };
}

export const useChannelsStore = create<ChannelsState>((set, get) => ({
  sportsChannels: [],
  sportsIndex: null,
  countries: [],
  categories: [],
  allChannels: null,
  allIndex: null,
  isLoadingInitial: false,
  isLoadingAll: false,
  isRefreshing: false,
  isInitialLoaded: false,
  lastUpdated: null,
  error: null,

  loadInitial: async () => {
    if (get().isLoadingInitial) return;
    set({ isLoadingInitial: true, error: null });

    try {
      const raw = await fetchM3U(M3U_SPORTS_URL);
      const channels = parseAndIndex(raw);
      const meta = buildMetadata(channels);
      set({
        sportsChannels: channels,
        sportsIndex: meta.index,
        countries: meta.countries,
        categories: meta.categories,
        isLoadingInitial: false,
        isInitialLoaded: true,
        lastUpdated: Date.now(),
      });
    } catch (err) {
      set({
        error: err instanceof Error ? err.message : String(err),
        isLoadingInitial: false,
      });
    }
  },

  loadAll: async () => {
    if (get().allChannels || get().isLoadingAll) return;
    set({ isLoadingAll: true });

    try {
      const raw = await fetchM3U(M3U_ALL_URL);
      const channels = parseAndIndex(raw);
      const index = buildSearchIndex(channels);
      set({
        allChannels: channels,
        allIndex: index,
        isLoadingAll: false,
        lastUpdated: Date.now(),
      });
    } catch (err) {
      set({
        error: err instanceof Error ? err.message : String(err),
        isLoadingAll: false,
      });
    }
  },

  refresh: async () => {
    if (get().isRefreshing) return;
    set({ isRefreshing: true, error: null });

    try {
      const [sportsText, allText] = await Promise.all([
        fetchM3U(M3U_SPORTS_URL),
        fetchM3U(M3U_ALL_URL),
      ]);

      const sports = parseAndIndex(sportsText);
      const all = parseAndIndex(allText);
      const meta = buildMetadata(sports);
      const allIndex = buildSearchIndex(all);

      set({
        sportsChannels: sports,
        sportsIndex: meta.index,
        countries: meta.countries,
        categories: meta.categories,
        allChannels: all,
        allIndex,
        isRefreshing: false,
        lastUpdated: Date.now(),
      });
    } catch (err) {
      set({
        error: err instanceof Error ? err.message : String(err),
        isRefreshing: false,
      });
    }
  },

  searchSports: (q: string) => {
    const { sportsIndex, sportsChannels } = get();
    if (!sportsIndex) return [];
    return searchChannels(sportsIndex, sportsChannels, q);
  },

  searchAll: (q: string) => {
    const { allIndex, allChannels } = get();
    if (!allIndex || !allChannels) return [];
    return searchChannels(allIndex, allChannels, q);
  },
}));
